import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box } from '@mui/material'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import UserManagementAPI from 'api/faber/UserManagementAPI'
import { AxiosError } from 'axios'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import ColorBox from 'common/Box/ColorWhiteBox'
import NormalText from 'common/Typography/NormalText'
import { FaberResponse } from 'helpers/backendTypes'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { SearchToolbar } from 'pages/OTPacks/sharedComponents'
import { CustomerDetails,UserOrderStatusItem } from 'types/api/faber/userOrderStatus.type'

import ClickListCell from './ClickListCell'

export default function SearchUser() {
  const [rows, setRows] = useState<UserOrderStatusItem[]>([])
  const [statusChecked] = useState<string[]>([])
  const [bundleChecked] = useState<string[]>([])

  const [tempSearchValue, setTempSearchValue] = useState<string>('')

  const [startDate] = useState<Date>(new Date(2022, 9, 1))
  const [endDate] = useState<Date>(changeMonth(new Date(), +3))
  const [sortDate, setSortDate] = useState<string>('DESC')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')

  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Email', 'Order ID']

  const queryOptions = React.useMemo(
    () => ({
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      filterStartDate: startDate,
      filterEndDate: endDate,
      sortDate
    }),
    [
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      startDate,
      endDate,
      sortDate
    ]
  )

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel?.[0]?.sort === 'asc') {
        setSortDate('ASC')
      } else {
        setSortDate('DESC')
      }
    },
    []
  )

  const { data: getAllUserOrderStatus, isLoading: isGetAllUsersLoading } = useQuery(
    [
      'getAllUserOrderStatus',
      [queryOptions.searchValue, queryOptions.searchCriteria]
    ],
    async () =>
      queryOptions.searchValue.length > 0
        ? UserManagementAPI.getAllUserOrderStatus({
            faberToken: faberToken,
            searchCriteria: queryOptions.searchCriteria,
            searchValue: queryOptions.searchValue
          })
        : null,
    {
      onSuccess: (res) => res,
      onError: (e: AxiosError<FaberResponse>) => {
        toast.error(e.response?.data.message)
        setRows([])
      }
    }
  )

  useEffect(() => {
    if (getAllUserOrderStatus) {
      const payload = getAllUserOrderStatus?.data?.data
      const items: UserOrderStatusItem[] = Array.isArray(payload) ? payload : []
      const mapped: UserOrderStatusItem[] = items.map(
        ({
          id,
          tenant_id,
          order_id,
          status,
          catalog_ids,
          catalog_details,
          customer_details
        }) => ({
          id,
          tenant_id,
          order_id,
          status,
          catalog_ids,
          catalog_details,
          customer_details
        })
      )

      setRows(mapped)
    } else {
      setRows([])
    }
  }, [getAllUserOrderStatus])

  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempSearchValue(e.target.value)
  }

  const onSearchSubmit = () => {
    const trimmedValue = tempSearchValue.trim()

    const hasInvalidCharsEmail = /[^a-zA-Z0-9@._+-]/.test(trimmedValue)
    const hasInvalidCharsName = /[^a-zA-Z0-9 ._-]/.test(trimmedValue)

    if (
      (searchCriteria === 'Email' && hasInvalidCharsEmail) ||
      (searchCriteria === 'Name' && hasInvalidCharsName)
    ) {
      toast.error('Search value contains invalid characters.')
      return
    }

    setSearchValue(trimmedValue)
  }

  const handleSearchCriteriaChange = (_e: unknown, v: string) => {
    setSearchCriteria(v)
  }

  const columns: GridColDef[] = [
    { field: 'tenant_id', headerName: 'Tenant ID', width: 170 },
    { field: 'order_id', headerName: 'Order ID', width: 320 },
    { field: 'status', headerName: 'Status', width: 140 },

    {
      field: 'catalog_ids',
      headerName: 'Catalog IDs',
      width: 240,
      sortable: false,
      renderCell: (params: { row: UserOrderStatusItem }) => (
        <NormalText>{(params.row.catalog_ids || []).join(', ')}</NormalText>
      )
    },

    {
      field: 'catalog_details',
      headerName: 'Catalog Details',
      width: 280,
      sortable: false,
      renderCell: (params) => (
        <ClickListCell
          title='Catalog IDs'
          label='Catalog IDs'
          items={params.row.catalog_ids || []}
          getItemLabel={(id) => String(id)}
        />
      )
    },
    {
      field: 'customer_email',
      headerName: 'Customer Email',
      width: 420,
      sortable: false,
      renderCell: (params: { row: UserOrderStatusItem }) => {
        const customers = params.row.customer_details || []
        if (customers.length === 0) return <NormalText>-</NormalText>

        return <NormalText>{customers[0].customer_email}</NormalText>
      }
    },
    {
  field: 'customer_details',
  headerName: 'Customer Details',
  width: 420,
  sortable: false,
  renderCell: (params: { row: UserOrderStatusItem }) => (
    <ClickListCell<CustomerDetails>
      title='Customer Details'
      label='Customers'
      items={(params.row.customer_details || []) as CustomerDetails[]}
      getItemLabel={(c) => `${c.firstname} ${c.lastname}`}
    />
  )
}

  ]

  return (
    <Box>
      <ColorBox>
        <Box>
          <NormalText isBold>
            Please type customer details in the search bar to begin
          </NormalText>

          <MUIDataGrid
            getRowId={(row) => row.id}
            tableHeight='750px'
            rows={rows}
            columns={columns}
            loading={isGetAllUsersLoading}
            disableColumnMenu={true}
            sortingMode='server'
            sortingOrder={['desc', 'asc']}
            onSortModelChange={handleSortModelChange}
            components={{ Toolbar: SearchToolbar }}
            componentsProps={{
              toolbar: {
                searchBar: 'server',
                serverSearchBarProps: {
                  onSubmit: onSearchSubmit,
                  onChange: onSearchValueChange,
                  options: searchOptions,
                  defaultValue: searchCriteria,
                  handleChange: handleSearchCriteriaChange
                }
              }
            }}
          />
        </Box>
      </ColorBox>
    </Box>
  )
}
